import type { StudentAssignmentCubit } from '@breakoutlearning/firebase-repository/cubits/StudentAssignmentCubit'
import type { SlideDeckMaterial } from '@breakoutlearning/firebase-repository/models/SlideDeckMaterial'
import { Spinner } from 'components/Spinner'
import { Dialog } from 'components/dialogs/Dialog'
import { DialogCloseButton } from 'components/dialogs/DialogCloseButton'
import { observer } from 'mobx-react-lite'
import { CourseMaterialListItem } from 'components/slide-deck-material-components/CourseMaterialListItem'
import { CourseMaterialsInstructionsMarkdown } from 'components/CourseMaterialsInstructionsMarkdown'
import { useEffect, useId } from 'react'
import { useTranslationTyped } from 'i18n/i18n'

// Shared with the admin participant-view preview (CourseMaterialsPreviewDialog).
export function MaterialsModalBody({
  headingId,
  slideDeckId,
  slideDeckName,
  slideDeckVersion,
  instructions,
  loading,
  materials,
  onViewTap,
}: {
  headingId: string
  slideDeckId: string
  slideDeckName: string
  slideDeckVersion: string
  instructions?: string
  loading: boolean
  materials: SlideDeckMaterial[]
  onViewTap?: (material: SlideDeckMaterial) => void
}) {
  const { tt } = useTranslationTyped()
  return (
    <div className="mx-auto flex h-full flex-col overflow-hidden!">
      <h1 id={headingId} className="mb-2 text-headline-large">
        {tt.student_assignment.course_materials()}
      </h1>
      {instructions ? (
        <div className="mb-5">
          <CourseMaterialsInstructionsMarkdown>
            {instructions}
          </CourseMaterialsInstructionsMarkdown>
        </div>
      ) : (
        <p className="mb-5 text-body-large text-on-surface-var">
          {tt.student_assignment.course_materials_info()}
        </p>
      )}
      {loading ? (
        <div className="my-10 flex flex-row items-center justify-center">
          <Spinner />
        </div>
      ) : (
        <div className="h-full grow overflow-auto">
          {materials.map((material) => (
            <CourseMaterialListItem
              data-testid={`course-material-item-${material.id}`}
              slideDeckVersion={slideDeckVersion}
              key={material.id}
              className="mb-2 flex h-[62px] flex-row items-center justify-between gap-3 rounded-2xl bg-light-grey p-5"
              slideDeckName={slideDeckName}
              slideDeckId={slideDeckId}
              material={material}
              onViewTap={onViewTap && (() => onViewTap(material))}
            />
          ))}
        </div>
      )}
    </div>
  )
}

const StudentMaterialsModalBody = observer(function StudentMaterialsModalBody({
  cubit,
  headingId,
}: {
  cubit: StudentAssignmentCubit
  headingId: string
}) {
  // Re-fetch materials if any signed URL has expired (6h TTL)
  useEffect(() => {
    cubit.ensureFreshMaterialUrls()
  }, [cubit])

  return (
    <MaterialsModalBody
      headingId={headingId}
      slideDeckId={cubit.slideDeck.id}
      slideDeckName={cubit.slideDeck.data.slideDeckName}
      slideDeckVersion={cubit.slideDeck.data.slideDeckVersion}
      instructions={cubit.slideDeck.data.slideDeckCourseMaterialsInstructions?.trim()}
      loading={cubit.materials.isLoading || cubit.isRefreshingMaterials}
      materials={cubit.availableStudentMaterials}
      onViewTap={(material) => {
        cubit.logEvent('student_view_course_material', {
          material_id: material.id,
        })
      }}
    />
  )
})

export function MaterialsModal({ cubit }: { cubit: StudentAssignmentCubit }) {
  const headingId = useId()
  return (
    <Dialog size="md" testId="materials-modal" aria-labelledby={headingId}>
      <DialogCloseButton />
      <StudentMaterialsModalBody cubit={cubit} headingId={headingId} />
    </Dialog>
  )
}
